import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Add,
  ArrowBack,
  AttachFile,
  Close,
  Delete,
  Download,
  Edit,
  NotificationsActive,
  Search,
  Warning,
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { API_BASE } from '../api';

ModuleRegistry.registerModules([AllCommunityModule]);

const API = `${API_BASE}/processos-admin`;

const STATUS_OPTS = ['Em andamento', 'Aprovado', 'Deferido', 'Comunique-se', 'Indeferido', 'Arquivado', 'Suspenso'];
const CLASSE_OPTS = ['A', 'B', 'C', 'D'];

const STATUS_COLOR = {
  'Aprovado':     { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  'Deferido':     { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  'Em andamento': { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  'Comunique-se': { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  'Suspenso':     { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  'Indeferido':   { bg: '#fce4ec', color: '#c62828', border: '#f48fb1' },
  'Arquivado':    { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' },
};

const CLASSE_COLOR = {
  A: '#7b1fa2',
  B: '#1565c0',
  C: '#e65100',
  D: '#00695c',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function isAtrasado(iso) {
  if (!iso) return false;
  return new Date(iso + 'T00:00:00') < new Date(new Date().toDateString());
}

function isProximo(iso) {
  if (!iso) return false;
  const d = new Date(iso + 'T00:00:00');
  const now = new Date(new Date().toDateString());
  const in15 = new Date(now); in15.setDate(in15.getDate() + 15);
  return d >= now && d <= in15;
}

const EMPTY = {
  numero_processo: '', interessado: '', orgao: '', tipo: '', status: 'Em andamento',
  responsavel: '', classe: '', data_entrada: '', data_vistoria: '', data_reuniao: '',
  data_entrega: '', data_emissao: '', data_prazo: '', hora_reuniao: '', revisao: '',
  comunique_se: false, observacoes: '', cliente_id: '',
};

// ── Célula de status ──────────────────────────────────────────────────────────
function StatusCell({ value }) {
  const s = STATUS_COLOR[value] || { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Box sx={{
        px: 1, py: 0.2, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700,
        bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`,
        whiteSpace: 'nowrap',
      }}>{value || '—'}</Box>
    </Box>
  );
}

// ── Célula de classe ──────────────────────────────────────────────────────────
function ClasseCell({ value }) {
  if (!value) return <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#9e9e9e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>—</Typography>
    </Box>
  </Box>;
  const c = CLASSE_COLOR[value] || '#9e9e9e';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: c, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{value}</Typography>
      </Box>
    </Box>
  );
}

// ── Célula de prazo ───────────────────────────────────────────────────────────
function PrazoCell({ value }) {
  const atrasado = isAtrasado(value);
  const proximo = isProximo(value);
  const color = atrasado ? '#d32f2f' : proximo ? '#e65100' : 'inherit';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Typography sx={{ fontSize: '0.8rem', color, fontWeight: atrasado || proximo ? 700 : 400 }}>
        {fmtDate(value)}
      </Typography>
    </Box>
  );
}

const TIPO_EVOLUCAO_OPTS = [
  { value: 'evolucao', label: 'Evolução' },
  { value: 'comunique_se', label: 'Comunique-se' },
  { value: 'documento', label: 'Documento' },
  { value: 'prazo', label: 'Prazo' },
  { value: 'reuniao', label: 'Reunião' },
  { value: 'deferido', label: 'Deferido' },
  { value: 'indeferido', label: 'Indeferido' },
  { value: 'reconsideracao_despacho', label: 'Reconsideração de despacho' },
  { value: 'arquivado', label: 'Arquivado' },
  { value: 'outro', label: 'Outro' },
];

const TIPO_COLOR = {
  evolucao:     { bg: '#e3f2fd', color: '#1565c0' },
  comunique_se: { bg: '#fff3e0', color: '#e65100' },
  documento:    { bg: '#f3e5f5', color: '#7b1fa2' },
  prazo:        { bg: '#fce4ec', color: '#c62828' },
  reuniao:      { bg: '#e8f5e9', color: '#2e7d32' },
  outro:        { bg: '#f5f5f5', color: '#616161' },
};

// ── Seção de evoluções dentro do modal ────────────────────────────────────────
function EvolucoesSection({ processoId, token, apiBase }) {
  const headers = useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);
  const API_EV = `${apiBase}/processos-admin/${processoId}/evolucoes`;

  const [evolucoes, setEvolucoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ tipo: 'evolucao', data: new Date().toISOString().slice(0, 10), descricao: '' });
  const [addFiles, setAddFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fileInputRef = useRef();

  const fetchEvolucoes = useCallback(async () => {
    if (!processoId) return;
    setLoading(true);
    try {
      const r = await fetch(API_EV, { headers });
      const d = await r.json();
      setEvolucoes(Array.isArray(d) ? d : []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [API_EV, headers, processoId]);

  useEffect(() => { fetchEvolucoes(); }, [fetchEvolucoes]);

  const handleAdd = async () => {
    if (!addForm.descricao.trim() && addFiles.length === 0) {
      setErr('Informe a descrição ou anexe um documento'); return;
    }
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('tipo', addForm.tipo);
      fd.append('data', addForm.data);
      fd.append('descricao', addForm.descricao);
      addFiles.forEach(f => fd.append('documentos[]', f));
      const r = await fetch(API_EV, { method: 'POST', headers, body: fd });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d?.errors?.join(', ') || `Erro ${r.status}`); }
      setAddForm({ tipo: 'evolucao', data: new Date().toISOString().slice(0, 10), descricao: '' });
      setAddFiles([]);
      setAddOpen(false);
      fetchEvolucoes();
    } catch (e) { setErr(e.message || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta evolução e seus anexos?')) return;
    await fetch(`${API_EV}/${id}`, { method: 'DELETE', headers });
    fetchEvolucoes();
  };

  const downloadUrl = (evolucaoId, docUrl) =>
    `${apiBase}/processos-admin/${processoId}/evolucoes/${evolucaoId}/download?path=${encodeURIComponent(docUrl)}`;

  const tipoLabel = (t) => TIPO_EVOLUCAO_OPTS.find(o => o.value === t)?.label || t;

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Evoluções / Comunique-se
        </Typography>
        <Button size="small" startIcon={<Add />} onClick={() => setAddOpen(o => !o)}>
          {addOpen ? 'Cancelar' : 'Adicionar'}
        </Button>
      </Stack>

      {addOpen && (
        <Box sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
          {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr('')}>{err}</Alert>}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextField label="Tipo" size="small" fullWidth select value={addForm.tipo}
                onChange={e => setAddForm(f => ({ ...f, tipo: e.target.value }))}>
                {TIPO_EVOLUCAO_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Data" type="date" size="small" fullWidth
                value={addForm.data} onChange={e => setAddForm(f => ({ ...f, data: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" size="small" startIcon={<AttachFile />} fullWidth
                sx={{ height: 40 }}
                onClick={() => fileInputRef.current?.click()}>
                {addFiles.length > 0 ? `${addFiles.length} arquivo(s)` : 'Anexar'}
              </Button>
              <input ref={fileInputRef} type="file" multiple hidden
                onChange={e => setAddFiles(Array.from(e.target.files))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descrição" size="small" fullWidth multiline rows={2}
                value={addForm.descricao}
                onChange={e => setAddForm(f => ({ ...f, descricao: e.target.value }))} />
            </Grid>
          </Grid>
          {addFiles.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {addFiles.map((f, i) => (
                <Chip key={i} label={f.name} size="small" sx={{ mr: 0.5, mt: 0.5 }}
                  onDelete={() => setAddFiles(prev => prev.filter((_, j) => j !== i))} />
              ))}
            </Box>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button variant="contained" size="small" onClick={handleAdd} disabled={saving}
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : undefined}>
              {saving ? 'Salvando...' : 'Salvar evolução'}
            </Button>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
      ) : evolucoes.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
          Nenhuma evolução registrada
        </Typography>
      ) : (
        <List dense disablePadding>
          {evolucoes.map((ev) => {
            const tc = TIPO_COLOR[ev.tipo] || TIPO_COLOR.outro;
            const docs = Array.isArray(ev.documentos) ? ev.documentos : [];
            return (
              <ListItem key={ev.id} disablePadding alignItems="flex-start"
                sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <ListItemText
                  disableTypography
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.4 }}>
                      <Box sx={{
                        px: 0.8, py: 0.1, borderRadius: 1, fontSize: '0.68rem', fontWeight: 700,
                        bgcolor: tc.bg, color: tc.color, whiteSpace: 'nowrap',
                      }}>
                        {tipoLabel(ev.tipo)}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {ev.data ? new Date(ev.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Box>
                      {ev.descricao && (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: docs.length ? 0.5 : 0 }}>
                          {ev.descricao}
                        </Typography>
                      )}
                      {docs.map((doc, i) => {
                        const docName = doc.name || doc.url?.split('/').pop() || `Arquivo ${i + 1}`;
                        return (
                          <Chip
                            key={i}
                            icon={<Download sx={{ fontSize: 13 }} />}
                            label={docName}
                            size="small"
                            clickable
                            component="a"
                            href={downloadUrl(ev.id, doc.url || doc)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mr: 0.5, mt: 0.3, fontSize: '0.7rem', maxWidth: 200 }}
                          />
                        );
                      })}
                    </Box>
                  }
                />
                <IconButton size="small" color="error" sx={{ mt: 0.3, ml: 0.5 }}
                  onClick={() => handleDelete(ev.id)}>
                  <Delete sx={{ fontSize: 14 }} />
                </IconButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}

// ── Modal de visualização ─────────────────────────────────────────────────────
function ProcessoViewDialog({ open, onClose, onEdit, row, clientesMap, token, apiBase }) {
  if (!row) return null;
  const s = STATUS_COLOR[row.status] || { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' };
  const cc = CLASSE_COLOR[row.classe];

  const field = (label, value) => (
    <Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.2 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.88rem', fontWeight: 500, color: value ? 'text.primary' : 'text.disabled' }}>
        {value || '—'}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          {cc && (
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: cc, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.3 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#fff' }}>{row.classe}</Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, wordBreak: 'break-word' }}>
              {row.numero_processo}
            </Typography>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Box sx={{ px: 1, py: 0.2, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700, bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                {row.status}
              </Box>
              {row.comunique_se && (
                <Box sx={{ px: 1, py: 0.2, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }}>
                  Comunique-se
                </Box>
              )}
            </Stack>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box sx={{ gridColumn: 'span 2' }}>
            {field('Cliente', clientesMap[String(row.cliente_id)])}
          </Box>
          <Box sx={{ gridColumn: 'span 2' }}>
            {field('Interessado / Requerente', row.interessado)}
          </Box>
          <Box sx={{ gridColumn: 'span 2' }}>
            {field('Órgão', row.orgao)}
          </Box>
          {field('Tipo', row.tipo)}
          {field('Responsável', row.responsavel)}
          {field('Revisão', row.revisao)}
          {field('Hora', row.hora_reuniao)}
          {field('Data entrada', fmtDate(row.data_entrada))}
          {field('Data vistoria', fmtDate(row.data_vistoria))}
          {field('Data reunião', fmtDate(row.data_reuniao))}
          {field('Prazo / Vencimento', fmtDate(row.data_prazo))}
          {field('Data entrega', fmtDate(row.data_entrega))}
          {field('Data emissão', fmtDate(row.data_emissao))}
          {row.observacoes && (
            <Box sx={{ gridColumn: 'span 2' }}>
              {field('Observações', row.observacoes)}
            </Box>
          )}
        </Box>

        <EvolucoesSection processoId={row.id} token={token} apiBase={apiBase} />

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button onClick={onClose}>Fechar</Button>
          <Button variant="contained" onClick={() => { onClose(); onEdit(row); }}>
            Editar
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ── Modal add/edit ────────────────────────────────────────────────────────────
function ProcessoModal({ open, onClose, onSave, initial, clientes, token, apiBase }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) { setForm(initial ? { ...EMPTY, ...initial } : { ...EMPTY }); setErr(''); }
  }, [open, initial]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const dateField = (label, key) => (
    <TextField label={label} type="date" size="small" fullWidth
      value={form[key] || ''} onChange={e => set(key, e.target.value)}
      InputLabelProps={{ shrink: true }} />
  );

  const handleSave = async () => {
    if (!form.numero_processo.trim()) { setErr('Número do processo é obrigatório'); return; }
    if (!form.cliente_id) { setErr('Cliente é obrigatório'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); }
    catch (e) { setErr(e.message || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const two = { gridColumn: 'span 2' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '95vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        {initial ? 'Editar processo' : 'Novo processo'}
        <IconButton onClick={onClose} size="small" sx={{ ml: 'auto' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 0.5 }}>

          {/* Row: Nº Processo | Classe */}
          <TextField label="Nº Processo *" size="small" fullWidth
            value={form.numero_processo} onChange={e => set('numero_processo', e.target.value)} />
          <TextField label="Classe" size="small" fullWidth select
            value={form.classe} onChange={e => set('classe', e.target.value)}>
            <MenuItem value="">—</MenuItem>
            {CLASSE_OPTS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>

          {/* Row: Cliente – full width */}
          <Box sx={two}>
            <Autocomplete
              sx={{ width: '100%' }}
              options={clientes}
              getOptionLabel={o => o?.nome || ''}
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              value={clientes.find(c => c.id === Number(form.cliente_id)) || null}
              onChange={(_, v) => set('cliente_id', v?.id || '')}
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={p => (
                <TextField {...p} label="Cliente *" size="small" fullWidth
                  error={!form.cliente_id} helperText={!form.cliente_id ? 'Selecione o cliente' : ''} />
              )}
            />
          </Box>

          {/* Row: Interessado – full width */}
          <TextField sx={two} label="Interessado / Requerente" size="small" fullWidth
            value={form.interessado} onChange={e => set('interessado', e.target.value)} />

          {/* Row: Órgão – full width */}
          <TextField sx={two} label="Órgão" size="small" fullWidth
            value={form.orgao} onChange={e => set('orgao', e.target.value)} />

          {/* Row: Tipo | Responsável */}
          <TextField label="Tipo" size="small" fullWidth
            value={form.tipo} onChange={e => set('tipo', e.target.value)} />
          <TextField label="Responsável" size="small" fullWidth
            value={form.responsavel} onChange={e => set('responsavel', e.target.value)} />

          {/* Row: Status | Revisão */}
          <TextField label="Status" size="small" fullWidth select
            value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUS_OPTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField label="Revisão" size="small" fullWidth
            value={form.revisao} onChange={e => set('revisao', e.target.value)} placeholder="R.00" />

          {/* Row: Hora | Data entrada */}
          <TextField label="Hora" size="small" fullWidth
            value={form.hora_reuniao} onChange={e => set('hora_reuniao', e.target.value)} placeholder="09:00" />
          {dateField('Data entrada', 'data_entrada')}

          {/* Row: Data vistoria | Data reunião */}
          {dateField('Data vistoria', 'data_vistoria')}
          {dateField('Data reunião', 'data_reuniao')}

          {/* Row: Prazo | Data entrega */}
          {dateField('Prazo / Vencimento', 'data_prazo')}
          {dateField('Data entrega', 'data_entrega')}

          {/* Row: Data emissão | (blank) */}
          {dateField('Data emissão', 'data_emissao')}
          <Box />

          {/* Row: Comunique-se – full width */}
          <Box sx={two}>
            <FormControlLabel
              control={<Checkbox checked={!!form.comunique_se} onChange={e => set('comunique_se', e.target.checked)} />}
              label="Comunique-se pendente"
            />
          </Box>

          {/* Row: Observações – full width */}
          <TextField sx={two} label="Observações" size="small" fullWidth multiline rows={3}
            value={form.observacoes} onChange={e => set('observacoes', e.target.value)} />

        </Box>

        {initial?.id && (
          <EvolucoesSection processoId={initial.id} token={token} apiBase={apiBase} />
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ProcessosFinanceiros() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { token } = useAuth();
  const headers = useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);
  const jsonHeaders = useMemo(() => ({ ...headers, 'Content-Type': 'application/json' }), [headers]);

  const gridRef = useRef();

  // Dados
  const [rows, setRows] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [search, setSearch] = useState('');
  const [filterResp, setFilterResp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClasse, setFilterClasse] = useState('');
  const [filterCliente, setFilterCliente] = useState('');

  // Tabs: 0=Processos, 1=Comunique-se, 2=Alertas, 3=Histórico
  const [tab, setTab] = useState(0);

  // Modal edição
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  // Modal visualização
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  // Snack
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const showSnack = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(API, { headers });
      const d = await r.json();
      setRows(Array.isArray(d) ? d : []);
    } catch { showSnack('Erro ao carregar processos', 'error'); }
    finally { setLoading(false); }
  }, [headers, token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/clientes`, { headers })
      .then(r => r.json())
      .then(d => setClientes(Array.isArray(d) ? d : (d.clientes || [])))
      .catch(() => {});
  }, [headers, token]);

  // Listas únicas para filtros
  const responsaveis = useMemo(() => [...new Set(rows.map(r => r.responsavel).filter(Boolean))].sort(), [rows]);
  const statuses = useMemo(() => [...new Set(rows.map(r => r.status).filter(Boolean))].sort(), [rows]);
  const classes = useMemo(() => [...new Set(rows.map(r => r.classe).filter(Boolean))].sort(), [rows]);

  // Mapa id→nome de clientes
  const clientesMap = useMemo(() => Object.fromEntries(clientes.map(c => [String(c.id), c.nome])), [clientes]);

  // Filtro de busca/dropdown aplicado
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      if (filterResp && r.responsavel !== filterResp) return false;
      if (filterStatus && r.status !== filterStatus) return false;
      if (filterClasse && r.classe !== filterClasse) return false;
      if (filterCliente && String(r.cliente_id) !== String(filterCliente)) return false;
      if (!q) return true;
      return [r.numero_processo, r.interessado, r.orgao, r.tipo, r.responsavel, r.status, clientesMap[String(r.cliente_id)] || '']
        .some(v => (v || '').toLowerCase().includes(q));
    });
  }, [rows, search, filterResp, filterStatus, filterClasse, filterCliente, clientesMap]);

  // Tab subsets
  const tabRows = useMemo(() => {
    switch (tab) {
      case 1: return filtered.filter(r => r.comunique_se);
      case 2: return filtered.filter(r => isAtrasado(r.data_prazo) || isProximo(r.data_prazo));
      case 3: return [...filtered].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      default: return filtered;
    }
  }, [filtered, tab]);

  // Badges de contagem
  const countComunique = useMemo(() => filtered.filter(r => r.comunique_se).length, [filtered]);
  const countAlertas = useMemo(() => filtered.filter(r => isAtrasado(r.data_prazo) || isProximo(r.data_prazo)).length, [filtered]);

  // Salvar (create/update)
  const handleSave = async (form) => {
    const isEdit = !!editItem;
    const url = isEdit ? `${API}/${editItem.id}` : API;
    const method = isEdit ? 'PUT' : 'POST';
    const r = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(form) });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d?.errors?.join(', ') || `Erro ${r.status}`);
    }
    setModalOpen(false);
    setEditItem(null);
    showSnack(isEdit ? 'Processo atualizado!' : 'Processo criado!');
    fetchRows();
  };

  const handleDelete = async (id, numero) => {
    if (!window.confirm(`Excluir processo "${numero}"?`)) return;
    try {
      await fetch(`${API}/${id}`, { method: 'DELETE', headers });
      showSnack('Processo excluído');
      fetchRows();
    } catch { showSnack('Erro ao excluir', 'error'); }
  };

  const openEdit = (row) => { setEditItem(row); setModalOpen(true); };
  const openNew = () => { setEditItem(null); setModalOpen(true); };
  const openView = (row) => { setViewItem(row); setViewOpen(true); };

  // Colunas ag-grid
  const colDefs = useMemo(() => [
    {
      field: 'classe', headerName: 'CL.', width: 56, minWidth: 56, maxWidth: 56,
      cellRenderer: p => <ClasseCell value={p.value} />,
      sortable: true,
    },
    {
      field: 'numero_processo', headerName: 'PROCESSO', minWidth: 140, flex: 1.2,
      cellStyle: { fontWeight: 600, fontSize: '0.82rem' },
    },
    { field: 'interessado', headerName: 'INTERESSADO', minWidth: 150, flex: 1.5 },
    {
      field: 'cliente_id', headerName: 'CLIENTE', minWidth: 130, flex: 1.2,
      valueFormatter: p => clientesMap[String(p.value)] || '—',
      cellStyle: { fontSize: '0.82rem', color: '#1565c0', fontWeight: 500 },
    },
    { field: 'orgao', headerName: 'ÓRGÃO', minWidth: 130, flex: 1.2 },
    { field: 'tipo', headerName: 'TIPO', minWidth: 110, flex: 1 },
    { field: 'responsavel', headerName: 'RESPONSÁVEL', minWidth: 120, flex: 1 },
    {
      field: 'data_entrada', headerName: 'ENTRADA', width: 90, minWidth: 90,
      valueFormatter: p => fmtDate(p.value),
      cellStyle: { fontSize: '0.8rem' },
    },
    {
      field: 'data_vistoria', headerName: 'VISTORIA', width: 90, minWidth: 90,
      valueFormatter: p => fmtDate(p.value),
      cellStyle: { fontSize: '0.8rem' },
    },
    {
      field: 'data_reuniao', headerName: 'REUNIÃO', width: 88, minWidth: 88,
      valueFormatter: p => fmtDate(p.value),
      cellStyle: { fontSize: '0.8rem' },
    },
    { field: 'hora_reuniao', headerName: 'HORA', width: 70, minWidth: 70, cellStyle: { fontSize: '0.8rem' } },
    {
      field: 'data_prazo', headerName: 'PRAZO', width: 88, minWidth: 88,
      cellRenderer: p => <PrazoCell value={p.value} />,
    },
    { field: 'revisao', headerName: 'REV.', width: 66, minWidth: 66, cellStyle: { fontSize: '0.8rem', color: '#1565c0', fontWeight: 600 } },
    {
      field: 'status', headerName: 'STATUS', minWidth: 130, width: 140,
      cellRenderer: p => <StatusCell value={p.value} />,
    },
    {
      field: '__actions__', headerName: '', width: 76, minWidth: 76, maxWidth: 76,
      sortable: false, filter: false, pinned: 'right',
      cellRenderer: p => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, height: '100%' }}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(p.data); }} sx={{ p: 0.4 }}>
              <Edit sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); handleDelete(p.data.id, p.data.numero_processo); }} sx={{ p: 0.4 }}>
              <Delete sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [clientesMap]); // eslint-disable-line

  const isDark = theme.palette.mode === 'dark';

  const tabBadge = (label, count, color) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
      {label}
      {count > 0 && (
        <Box sx={{
          minWidth: 20, height: 20, px: 0.6, borderRadius: 10,
          bgcolor: color, color: '#fff', fontSize: '0.68rem', fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
        }}>{count}</Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: isDark ? '#0f172a' : '#f0f4f8', minHeight: '100vh' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/financeiro')} size="small"
          sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Processos</Typography>
          <Typography variant="caption" color="text.secondary">Acompanhamento e controle de processos administrativos</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openNew} size="small">
          Novo processo
        </Button>
      </Stack>

      {/* KPI cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Total',        value: filtered.length,  color: '#1B6EE8' },
          { label: 'Em andamento', value: filtered.filter(r => r.status === 'Em andamento').length, color: '#1A9B59' },
          { label: 'Comunique-se', value: countComunique,   color: '#D47E06' },
          { label: 'Alertas prazo', value: countAlertas,    color: '#D63030' },
        ].map(k => (
          <Grid item xs={6} sm={3} key={k.label}>
            <Paper sx={{ borderRadius: 2, borderTop: `3px solid ${k.color}`, p: 1.5, boxShadow: 1 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.5 }}>
                {k.label}
              </Typography>
              <Typography sx={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, color: k.color, fontVariantNumeric: 'tabular-nums' }}>
                {k.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Main card com tabs + filtros + grid */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 44 }}>
            <Tab label={tabBadge('Processos', filtered.length, '#1B6EE8')} sx={{ minHeight: 44, fontSize: '0.82rem', fontWeight: 600 }} />
            <Tab label={tabBadge('Comunique-se', countComunique, '#D47E06')} sx={{ minHeight: 44, fontSize: '0.82rem', fontWeight: 600 }} />
            <Tab label={tabBadge('Alertas', countAlertas, '#D63030')} sx={{ minHeight: 44, fontSize: '0.82rem', fontWeight: 600 }}
              icon={countAlertas > 0 ? <NotificationsActive sx={{ fontSize: 14, color: '#D63030' }} /> : undefined}
              iconPosition="start" />
            <Tab label="Histórico" sx={{ minHeight: 44, fontSize: '0.82rem', fontWeight: 600 }} />
          </Tabs>
        </Box>

        {/* Filtros */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ md: 'center' }}>
            <TextField size="small" placeholder="Buscar processo, interessado, órgão..."
              value={search} onChange={e => setSearch(e.target.value)}
              sx={{ flex: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
              }} />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select displayEmpty value={filterCliente} onChange={e => setFilterCliente(e.target.value)}>
                <MenuItem value="">Todos os clientes</MenuItem>
                {clientes.filter(c => rows.some(r => String(r.cliente_id) === String(c.id))).map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select displayEmpty value={filterResp} onChange={e => setFilterResp(e.target.value)}>
                <MenuItem value="">Todos os responsáveis</MenuItem>
                {responsaveis.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select displayEmpty value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="">Todos os status</MenuItem>
                {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select displayEmpty value={filterClasse} onChange={e => setFilterClasse(e.target.value)}>
                <MenuItem value="">Todas as classes</MenuItem>
                {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {tabRows.length} processo{tabRows.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </Box>

        {/* Grid */}
        <Box sx={{ height: 520, width: '100%' }}
          className={isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <AgGridReact
              ref={gridRef}
              rowData={tabRows}
              columnDefs={colDefs}
              rowHeight={40}
              headerHeight={38}
              defaultColDef={{
                sortable: true,
                resizable: true,
                suppressMovable: false,
                cellStyle: { display: 'flex', alignItems: 'center', fontSize: '0.82rem' },
              }}
              rowStyle={{ cursor: 'pointer' }}
              onRowClicked={p => openView(p.data)}
              suppressCellFocus
              animateRows
              pagination
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              localeText={{
                noRowsToShow: tab === 1 ? 'Nenhum comunique-se pendente' : tab === 2 ? 'Nenhum alerta de prazo' : 'Nenhum processo encontrado',
                page: 'Página', to: 'até', of: 'de', more: 'mais',
                firstPage: '«', previousPage: '‹', nextPage: '›', lastPage: '»',
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Aviso tab Alertas */}
      {tab === 2 && countAlertas === 0 && !loading && (
        <Paper sx={{ mt: 2, p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderLeft: '4px solid #2e7d32' }}>
          <Warning sx={{ color: '#2e7d32' }} />
          <Typography variant="body2">Nenhum processo com prazo próximo ou vencido.</Typography>
        </Paper>
      )}

      {/* Modal visualização */}
      <ProcessoViewDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        onEdit={openEdit}
        row={viewItem}
        clientesMap={clientesMap}
        token={token}
        apiBase={API_BASE}
      />

      {/* Modal edição */}
      <ProcessoModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditItem(null); }}
        onSave={handleSave}
        initial={editItem}
        clientes={clientes}
        token={token}
        apiBase={API_BASE}
      />

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
